/**
 * intent-handlers.ts
 *
 * Local "intent" fast-paths extracted verbatim from ChatBox.tsx so the
 * component stays lean. Each function returns a boolean `handled` flag; the
 * submit path short-circuits (skips the LLM round-trip) when it returns true.
 *
 * Behaviour preserved EXACTLY:
 *  - handleViewIntent never creates a module; it only opens/fullscreens an
 *    EXISTING pane and reads `focusModuleRef.current` (intentionally NOT in the
 *    callback dep array in ChatBox).
 *  - handleOpenObjectIntent dispatches a SYNCHRONOUS window CustomEvent whose
 *    handler mutates `detail.handled` / `detail.displayName`.
 *  - handleFilterIntent returns true SYNCHRONOUSLY for contact-scoped filters
 *    while an async IIFE (api.objects.getObjectsByType) resolves later — it does
 *    NOT await the fetch.
 *
 * These are invoked from the in-component `useCallback` shells in ChatBox.tsx,
 * which retain their exact dependency arrays so hook order/count is unchanged.
 */
import type { RefObject } from 'react';
import { api } from '@/lib/api';
import type { Module } from '@/types/modules';

/**
 * Minimal shape of the active conversation these fast-paths depend on. Matches
 * the relevant subset of the Conversation type used in ChatBox.
 */
interface ActiveConversationLike {
  id: string;
  name?: string;
  modules?: Module[];
}

/** Metadata passed alongside a locally-injected message. */
interface LocalMessageMeta {
  routing?: string;
  durationMs?: number;
  intent?: string;
}

/**
 * Signature of the `addLocalMessages` callback surfaced by `useChat`
 * (see `useChat.types.ts` → `UseChatReturn.addLocalMessages`).
 */
type AddLocalMessages = (
  userContent: string,
  assistantContent: string,
  conversationId?: string,
  metadata?: LocalMessageMeta,
) => { userMessageId?: string; assistantMessageId: string };

type DispatchModuleCommand = (detail: Record<string, unknown>) => void;

type GetNextViewAckMessage = (
  viewLabel: string,
  mode: 'open' | 'switch' | 'create',
) => string;

type RunLocalViewIntentAction = (action: () => void) => void;

type FocusModule = (
  moduleId: string,
  options?: { anchorMessageId?: string | null; shouldScrollIntoView?: boolean },
) => void;

/** Dependencies captured by the ChatBox `handleViewIntent` useCallback. */
export interface HandleViewIntentDeps {
  activeConversation: ActiveConversationLike | null;
  addLocalMessages: AddLocalMessages;
  dispatchModuleCommand: DispatchModuleCommand;
  getNextViewAckMessage: GetNextViewAckMessage;
  runLocalViewIntentAction: RunLocalViewIntentAction;
  /**
   * Stable ref to ChatBox's `focusModule` (the exact type `useRef(focusModule)`
   * produces). Read via `.current` only — intentionally NOT in the callback dep
   * array, so the callback isn't recreated when focusModule changes.
   */
  focusModuleRef: RefObject<FocusModule>;
}

/**
 * Local fast-path for "show/open/view/switch to X" commands. Opens or
 * fullscreens an EXISTING pane without round-tripping the LLM. Never creates a
 * module. Returns true when handled.
 */
export function handleViewIntent(
  content: string,
  deps: HandleViewIntentDeps,
): boolean {
  const {
    activeConversation,
    addLocalMessages,
    dispatchModuleCommand,
    getNextViewAckMessage,
    runLocalViewIntentAction,
    focusModuleRef,
  } = deps;

  if (!activeConversation) return false;
  const conversationId = activeConversation.id;
  const lower = content.toLowerCase();
  const firstLine = lower.split(/\r?\n/)[0] || '';
  const viewCommandMatch = firstLine.match(
    /^\s*(?:(?:can|could|would)\s+you\s+|please\s+)?(?:show|open(?:\s+up)?|view|switch\s+to|take\s+me\s+to|bring\s+up|pull\s+up|go\s+to|jump\s+to)\b\s+(.+)/i
  );
  if (!viewCommandMatch) return false;
  let targetText = viewCommandMatch[1].trim();
  targetText = targetText.replace(/[.?!]+$/, '').replace(/\bplease\b\s*$/i, '').trim();
  targetText = targetText.replace(/^\bmy\b\s+/i, '');
  if (!targetText) return false;

  // Complex requests (> 5 words after verb) are not simple view commands —
  // let the agent handle them (e.g., "show me a pie chart of my tasks")
  const targetWords = targetText.split(/\s+/).filter(w => w.length > 0);
  if (targetWords.length > 5) return false;

  // Special handling for calendar - expand the existing event_list module to fullscreen
  const isCalendarRequest = /\bcalendars?\b/.test(targetText);

  if (isCalendarRequest) {
    const eventModule = (activeConversation.modules || []).find(
      (m) => !m.archived && m.type === 'event_list'
    );

    if (eventModule) {
      const acknowledgement = getNextViewAckMessage('Calendar', 'switch');

      runLocalViewIntentAction(() => {
        dispatchModuleCommand({
          action: 'updateModuleConfig',
          moduleId: eventModule.id,
          config: { fullscreen: true },
        });

        addLocalMessages(content, acknowledgement, conversationId, {
          routing: 'command:view:switch',
          durationMs: 0,
        });
      });
      return true;
    }
  }

  const presets: Array<{
    keyword: RegExp;
    type: Module['type'];
    label: string;
    objectType?: Module['objectType'];
    config?: Module['config'];
    size?: Module['size'];
  }> = [
    { keyword: /\bcalendars?\b/, type: 'event_list', label: 'Calendar', objectType: 'event' },
    { keyword: /\b(?:kanban|boards?)\b/, type: 'kanban', label: 'Kanban', objectType: 'task', config: { viewMode: 'kanban', show_completed: true } },
    { keyword: /\b(?:crm|pipelines?)\b/, type: 'crm', label: 'CRM Pipeline', objectType: 'contact' },
    { keyword: /\btasks?\b/, type: 'task_list', label: 'Kanban', objectType: 'task' },
    { keyword: /\bevents?\b/, type: 'event_list', label: 'Events', objectType: 'event' },
    { keyword: /\bcontacts?\b/, type: 'contact_list', label: 'Contacts', objectType: 'contact' },
    { keyword: /\b(notes?|docs?)\b/, type: 'drive', label: 'Drive', objectType: 'asset' },
    { keyword: /\bplaces?\b/, type: 'place_list', label: 'Places', objectType: 'place' },
  ];

  const preset = presets.find((entry) => entry.keyword.test(targetText));
  if (!preset) return false;

  const existing = (activeConversation.modules || []).find(
    (module) => !module.archived && module.type === preset.type
  );

  if (existing) {
    const acknowledgement = getNextViewAckMessage(preset.label, 'open');
    runLocalViewIntentAction(() => {
      const localMessageIds = addLocalMessages(content, acknowledgement, conversationId, {
        routing: 'command:view',
        durationMs: 0,
      });
      requestAnimationFrame(() => {
        focusModuleRef.current(existing.id, { anchorMessageId: localMessageIds.assistantMessageId });
      });
    });
    return true;
  }

  // Don't create modules from the local fast path.
  // Let the model/tool path resolve workspace-wide matches and disambiguation.
  return false;
}

/** Dependencies captured by the ChatBox `handleOpenObjectIntent` useCallback. */
export interface HandleOpenObjectIntentDeps {
  activeConversation: ActiveConversationLike | null;
  addLocalMessages: AddLocalMessages;
  runLocalViewIntentAction: RunLocalViewIntentAction;
}

/**
 * Fast-path: "open [name]" asks the active module to find and open an object by
 * name. Uses a synchronous window event so the module (which owns the data)
 * resolves the match.
 */
export function handleOpenObjectIntent(
  content: string,
  deps: HandleOpenObjectIntentDeps,
): boolean {
  const { activeConversation, addLocalMessages, runLocalViewIntentAction } = deps;

  if (!activeConversation) return false;
  const firstLine = content.split(/\r?\n/)[0] || '';
  const openMatch = firstLine.match(
    /^\s*(?:(?:can|could|would)\s+you\s+|please\s+)?(?:show|open(?:\s+up)?|view|pull\s+up|bring\s+up)\b\s+(.+)/i
  );
  if (!openMatch) return false;
  const targetName = openMatch[1].trim().replace(/[.?!]+$/, '').replace(/\bplease\b\s*$/i, '').trim();
  if (!targetName) return false;

  // Only handle short phrases (object names), not complex requests
  if (targetName.split(/\s+/).length > 5) return false;

  // Check if the target matches a module-type keyword — let handleViewIntent handle those
  if (/\b(tasks?|contacts?|events?|notes?|docs?|places?|stats?|calendars?|timelines?|kanban|boards?|crm|pipelines?|activity|email)\b/i.test(targetName)) return false;

  // Dispatch event for the active module to handle — module has the actual data
  const detail = { name: targetName, handled: false, displayName: '' };
  window.dispatchEvent(new CustomEvent('xavior:open-object-by-name', { detail }));

  if (detail.handled) {
    const conversationId = activeConversation.id;
    runLocalViewIntentAction(() => {
      addLocalMessages(content, `Opening ${detail.displayName || targetName}.`, conversationId, {
        routing: 'command:open-object',
        durationMs: 0,
      });
    });
    return true;
  }

  return false;
}

/** Dependencies captured by the ChatBox `handleFilterIntent` useCallback. */
export interface HandleFilterIntentDeps {
  activeConversation: ActiveConversationLike | null;
  addLocalMessages: AddLocalMessages;
  dispatchModuleCommand: DispatchModuleCommand;
  token: string | null;
}

/**
 * Parses filter commands: "clear filter", "only show X", "<contact>'s events".
 * For contact filters, fires an async fetch but returns true synchronously.
 */
export function handleFilterIntent(
  content: string,
  deps: HandleFilterIntentDeps,
): boolean {
  const { activeConversation, addLocalMessages, dispatchModuleCommand, token } = deps;

  if (!activeConversation) return false;
  const lower = content.toLowerCase();

  if (/(clear filter|show all|remove filter|reset filter)/i.test(lower)) {
    dispatchModuleCommand({ action: 'clearFilter' });
    addLocalMessages(content, 'Filter cleared. Showing all items.', activeConversation.id, {
      routing: 'command:filter:clear',
      durationMs: 0,
    });
    return true;
  }

  const match = content.match(/(?:only show|show only|just show|filter to|filter for|only show me)\s+(.+)/i);
  if (!match) return false;

  const filterText = match[1].trim().replace(/[.?!]+$/, '');
  if (!filterText) return false;

  const contactMatch =
    filterText.match(/^(.+?)'s\s+(?:events|event|calendar|timeline)$/i) ||
    filterText.match(/^(?:events|event|calendar|timeline)\s+(?:with|for)\s+(.+)$/i);

  if (contactMatch) {
    const contactName = contactMatch[1].trim().replace(/[.?!]+$/, '');
    if (contactName) {
      void (async () => {
        try {
          const result = await api.objects.getObjectsByType(token ?? null, 'contact', {
            conversationId: activeConversation.id,
            query: contactName,
            limit: 10,
          });
          const items = result.items ?? [];
          // Prefer exact match (case-insensitive) over partial matches
          const normalizedQuery = contactName.toLowerCase();
          const exactMatch = items.find((c) => {
            const name = (c.name || c.title || '').toLowerCase();
            return name === normalizedQuery;
          });
          const bestMatch = exactMatch || items[0];
          if (bestMatch) {
            const displayName = bestMatch.name || bestMatch.title || contactName;
            const nextFilter = `contact:${bestMatch.id}|${displayName}`;
            dispatchModuleCommand({ action: 'setFilter', filterText: nextFilter });
            // Note if we picked from multiple non-exact matches
            const note = !exactMatch && items.length > 1
              ? ` (${items.length} contacts matched, showing first)`
              : '';
            addLocalMessages(content, `Filtering events linked to "${displayName}".${note}`, activeConversation.id, {
              routing: 'command:filter:contact',
              durationMs: 0,
            });
            return;
          }
        } catch (error) {
          console.error('[ChatBox] Failed to resolve contact filter:', error);
        }

        dispatchModuleCommand({ action: 'setFilter', filterText });
        addLocalMessages(content, `Filtering view for "${filterText}".`, activeConversation.id, {
          routing: 'command:filter',
          durationMs: 0,
        });
      })();
      return true;
    }
  }

  dispatchModuleCommand({ action: 'setFilter', filterText });
  addLocalMessages(content, `Filtering view for "${filterText}".`, activeConversation.id, {
    routing: 'command:filter',
    durationMs: 0,
  });
  return true;
}
